import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Route, MapPin, Clock, Layers, CloudSun, Snowflake, AlertTriangle, CheckCircle } from 'lucide-react';
import { getMultiStopRoute, getGeocodingSuggestions, getStaticMapUrl } from '@/lib/mapbox';
import MapLoader from './MapLoader';
import { cn } from '@/lib/utils';

interface StopRecord {
  id: string;
  stop_number: number;
  stop_type: string;
  city: string;
  state: string;
  scheduled_date: string | null;
  scheduled_time_start: string | null;
  scheduled_time_end: string | null;
  name: string;
}

interface RouteMapModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  load: any;
  stops: StopRecord[];
  stopsLoading: boolean;
}

/* ── Weather heuristic (zero-cost, no API) ── */
const WINTER_STATES = new Set(['MT','ND','SD','MN','WI','MI','WY','CO','NE','IA','IL','IN','OH','PA','NY','VT','NH','ME','MA','CT','RI','NJ','WV','ID','UT']);
const SUMMER_HEAT_STATES = new Set(['AZ','NV','TX','NM','LA','MS','AL','FL','GA','SC']);

function getWeatherHeuristic(pickupDate: string | null, originState: string, destState: string) {
  const month = pickupDate ? new Date(pickupDate + 'T00:00:00').getMonth() + 1 : new Date().getMonth() + 1;
  const isWinter = month <= 3 || month >= 11;
  const isSummer = month >= 6 && month <= 8;

  if (isWinter && (WINTER_STATES.has(originState) || WINTER_STATES.has(destState))) {
    return { severity: 'moderate' as const, label: 'Winter weather risk', icon: Snowflake };
  }
  if (isSummer && (SUMMER_HEAT_STATES.has(originState) || SUMMER_HEAT_STATES.has(destState))) {
    return { severity: 'low' as const, label: 'Heat advisory possible', icon: CloudSun };
  }
  return { severity: 'none' as const, label: 'No major alerts', icon: CheckCircle };
}

const RouteMapModal: React.FC<RouteMapModalProps> = ({ open, onOpenChange, load, stops, stopsLoading }) => {
  const [mapLoading, setMapLoading] = useState(true);
  const [mapError, setMapError] = useState(false);
  const [staticUrl, setStaticUrl] = useState<string | null>(null);
  const [routeSummary, setRouteSummary] = useState<{ distanceText: string; durationText: string } | null>(null);
  const [hoveredStopIdx, setHoveredStopIdx] = useState<number | null>(null);
  const resolvedRef = useRef<{ key: string; coords: [number, number][]; labels: { type: string; label: string; num: number }[]; geometry: any } | null>(null);

  const originLabel = load ? `${load.origin_city || load.originCity || ''}, ${load.origin_state || load.originState || ''}` : '';
  const destLabel = load ? `${load.destination_city || load.destCity || ''}, ${load.destination_state || load.destState || ''}` : '';
  const totalMiles = load?.total_miles || load?.miles || null;
  const stopCount = stops.length;
  const pickupDate = load?.pickup_date || load?.pickupDate || null;
  const originState = load?.origin_state || load?.originState || '';
  const destState = load?.destination_state || load?.destState || '';

  const weather = getWeatherHeuristic(pickupDate, originState, destState);
  const stopsKey = stops.map(s => s.id).join(',');

  useEffect(() => {
    if (!open || !load) return;
    if (stopsLoading || stops.length === 0) return;

    setMapLoading(true);
    setMapError(false);
    setStaticUrl(null);
    setRouteSummary(null);

    const timeout = setTimeout(() => buildStaticMap(), 100);
    return () => clearTimeout(timeout);
  }, [open, load?.id, stopsKey, stopsLoading]);

  const buildStaticMap = async () => {
    try {
      const coords: [number, number][] = [];
      const stopLabels: { type: string; label: string; num: number }[] = [];

      for (const stop of stops) {
        let coord: [number, number] | null = null;

        if (stop.stop_number === 1 && stop.stop_type === 'pickup' && load.origin_latitude && load.origin_longitude) {
          coord = [load.origin_longitude, load.origin_latitude];
        } else if (stop === stops[stops.length - 1] && stop.stop_type === 'delivery' && load.destination_latitude && load.destination_longitude) {
          coord = [load.destination_longitude, load.destination_latitude];
        }

        if (!coord) {
          try {
            const data = await getGeocodingSuggestions(`${stop.city}, ${stop.state}`);
            if (data?.features?.[0]?.center) {
              coord = data.features[0].center;
            }
          } catch { /* skip */ }
        }

        if (coord) {
          coords.push(coord);
          stopLabels.push({ type: stop.stop_type, label: `${stop.city}, ${stop.state}`, num: stop.stop_number });
        }
      }

      if (coords.length < 2) { setMapError(true); setMapLoading(false); return; }

      const routeResult = await getMultiStopRoute(coords);
      const geometry = routeResult?.geometry || { type: 'LineString', coordinates: coords };

      if (routeResult) {
        setRouteSummary({ distanceText: routeResult.distanceText, durationText: routeResult.durationText });
      }

      resolvedRef.current = { key: stopsKey, coords, labels: stopLabels, geometry };

      const isDark = document.documentElement.classList.contains('dark');
      const stopMarkers = coords.map((c, i) => ({
        lng: c[0], lat: c[1], type: stopLabels[i].type, num: stopLabels[i].num,
      }));

      const url = await getStaticMapUrl(geometry, stopMarkers, { width: 800, height: 400, darkMode: isDark });

      if (url) {
        setStaticUrl(url);
        setMapLoading(false);
      } else {
        setMapError(true);
        setMapLoading(false);
      }
    } catch (err) {
      console.error('RouteMapModal static build error:', err);
      setMapError(true);
      setMapLoading(false);
    }
  };

  const formatStopDate = (d: string | null) => {
    if (!d) return '';
    try { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
    catch { return d; }
  };

  const formatTimeWindow = (start: string | null, end: string | null) => {
    if (!start) return '';
    const fmt = (t: string) => {
      try {
        const [h, m] = t.split(':');
        const d = new Date(); d.setHours(+h, +m);
        return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      } catch { return t; }
    };
    return end ? `${fmt(start)} – ${fmt(end)}` : fmt(start);
  };

  const WeatherIcon = weather.icon;
  const milesNum = totalMiles ? Number(totalMiles) : null;
  const isLongHaul = milesNum && milesNum > 1500;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto overflow-x-hidden p-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Route className="h-5 w-5 text-accent" />
            Route: {originLabel} → {destLabel}
          </DialogTitle>
        </DialogHeader>

        {/* Route summary strip */}
        {(routeSummary || milesNum || stopCount > 0) && (
          <div className="px-6 pb-2">
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              {(routeSummary?.distanceText || milesNum) && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {routeSummary?.distanceText || `${milesNum!.toLocaleString()} mi`}
                </span>
              )}
              {routeSummary?.durationText && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {routeSummary.durationText}
                </span>
              )}
              {stopCount > 0 && (
                <span className="flex items-center gap-1">
                  <Layers className="h-3.5 w-3.5" />
                  {stopCount} stops
                </span>
              )}
            </div>
            <Separator className="mt-3" />
          </div>
        )}

        {/* Map area — touch-pan-y prevents horizontal swipe pan on mobile */}
        <div className="relative w-full h-[400px] bg-muted overflow-hidden touch-pan-y" style={{ touchAction: 'pan-y' }}>
          {staticUrl ? (
            <img
              src={staticUrl}
              alt={`Route from ${originLabel} to ${destLabel}`}
              className="w-full h-full object-cover select-none"
              draggable={false}
              loading="eager"
            />
          ) : null}

          {mapLoading && !mapError && <MapLoader />}
          {mapError && (
            <div className="absolute inset-0 flex items-center justify-center bg-muted">
              <p className="text-sm text-muted-foreground">Route unavailable</p>
            </div>
          )}
        </div>

        {/* Route alert strip */}
        <div className="px-6 py-2.5 flex flex-wrap items-center gap-2">
          <Badge
            variant="outline"
            className={cn(
              "text-[11px] px-2 py-0.5 gap-1",
              weather.severity === 'none' && "border-primary/30 text-primary",
              weather.severity === 'low' && "border-yellow-500/30 text-yellow-600 dark:text-yellow-400",
              weather.severity === 'moderate' && "border-orange-500/30 text-orange-600 dark:text-orange-400",
            )}
          >
            <WeatherIcon className="h-3 w-3" />
            {weather.label}
          </Badge>
          {isLongHaul && (
            <Badge variant="outline" className="text-[11px] px-2 py-0.5 gap-1 border-muted-foreground/30 text-muted-foreground">
              <AlertTriangle className="h-3 w-3" />
              Long-haul: {milesNum!.toLocaleString()}+ mi
            </Badge>
          )}
        </div>

        <Separator className="mx-6" />

        {/* Stop list */}
        <div className="px-6 py-4 space-y-1">
          {stopsLoading ? (
            <p className="text-sm text-muted-foreground text-center py-4">Loading stops…</p>
          ) : (
            stops.map((stop, idx) => (
              <div
                key={stop.id}
                className={cn(
                  "flex items-start gap-3 py-1.5 px-2 rounded-md transition-colors duration-150 cursor-default",
                  hoveredStopIdx === idx && "bg-accent/10"
                )}
                onMouseEnter={() => setHoveredStopIdx(idx)}
                onMouseLeave={() => setHoveredStopIdx(null)}
              >
                <div className="flex flex-col items-center mt-0.5">
                  <div className={cn(
                    "h-3 w-3 rounded-full shrink-0 transition-transform duration-150",
                    stop.stop_type === 'pickup' ? 'bg-primary' : 'bg-destructive',
                    hoveredStopIdx === idx && 'scale-125'
                  )} />
                  {idx < stops.length - 1 && <div className="w-0.5 h-6 bg-border mt-0.5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-muted-foreground">{idx + 1}.</span>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                      {stop.stop_type === 'pickup' ? 'Pickup' : 'Delivery'}
                    </Badge>
                    <span className="text-sm font-semibold text-foreground">{stop.city}, {stop.state}</span>
                  </div>
                  <div className="flex items-center gap-2 ml-5">
                    {formatStopDate(stop.scheduled_date) && (
                      <span className="text-[11px] text-muted-foreground">{formatStopDate(stop.scheduled_date)}</span>
                    )}
                    {formatTimeWindow(stop.scheduled_time_start, stop.scheduled_time_end) && (
                      <span className="text-[11px] text-muted-foreground">
                        · {formatTimeWindow(stop.scheduled_time_start, stop.scheduled_time_end)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RouteMapModal;
